<template>
  <div id="metadata-table-body">

    <el-row justify="space-between">
      <el-col style="margin-top: 20px;"  :xs="{span: 24, offset: 0}" 
          :sm="{span: 24, offset: 0}" :md="{span: 17, offset: 1}" 
          :lg="{span: 17, offset: 1}" :xl="{span: 17, offset: 1}" align="left">
        <el-pagination @size-change="store.metadataTable.methods.handlePageChange"
          @current-change="store.metadataTable.methods.handlePageChange"
          :current-page.sync="store.metadataTable.tableSettings.query.page"
          :page-sizes="[10, 20, 50, 100]"
          :page-size.sync="store.metadataTable.tableSettings.query.pageSize"
          :layout="paginationLayout"
          :pager-count="5"
          :total="store.metadataTable.tableSettings.totalEntries"
          :style="`color: ${store.theme.viewMode.textColor[store.theme.viewMode.active]}`">
        </el-pagination>
      </el-col>

      <el-col style="margin-top: 20px;" :xs="{ span: 24, offset: 0 }" 
        :sm="{ span: 24 }" :md="{ span: 5 }" 
        :lg="{ span: 5 }" :xl="{ span: 5 }" align="right">
        <el-tooltip content="Table filters" placement="top">
          <el-button size="small" type="danger" style="margin-left: 10px" @click="filtersVisible = (filtersVisible) ? false: true">
            <i class="fas icon-fa-filter" style="font-size: 1.2em"></i>
          </el-button>
        </el-tooltip>
        
        <el-tooltip content="Show/Hide columns" placement="top">
          <el-dropdown>
              <el-button size="small" type="primary" style="margin-left: 10px">
                <i class="fas icon-fa-edit" style="font-size: 1.2em"></i>
              </el-button>
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item v-for="col in store.metadataTable.tableSettings.columns" :key="col.key">
                <el-checkbox v-model="col.show" @change="reloadTable">{{ col.title }}</el-checkbox>
              </el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
        </el-tooltip>

        <el-tooltip content="Download TSV" placement="top">
          <el-button size="small" type="info" style="margin-left: 10px" @click="downloadMetadataTSV">
            <i class="fas icon-fa-download" style="font-size: 1.2em"></i>
          </el-button>
        </el-tooltip>
      </el-col>
    </el-row>

    <el-row style="margin-top: 30px; font-family: 'Oxygen', Arial; min-height: 60vh;" v-loading="!store.metadataTable.tableSettings.show">
      <el-col :span="24" align="center">
        <el-table height="60vh" id="metadata-table" :data="store.metadataTable.tableSettings.tableData"
          style="border-radius: 5px"
          size="medium" :show-overflow-tooltip="true"
          :header-cell-style="headerCellStyle" v-if="store.metadataTable.tableSettings.show">

          <el-table-column label="Flags" width="60">
            <template slot-scope="props">
              <el-tooltip effect="dark" placement="top-start">
                <div slot="content">{{ flagContent(props) }}</div>
                <i class="fas icon-fa-flag" :style="`color: ${flagColor(props)}`"></i>
              </el-tooltip>
            </template>
          </el-table-column>
          
          <el-table-column 
            v-for="col in visibleColumns" 
            :key="col.key" :label="col.title" :prop="col.key" 
            :show-overflow-tooltip="true"
            :width="100 + col.title.length*5">
            <template slot="header" slot-scope="props">
              <div style="cursor: pointer" @click="sortTable(props.$index, props.column)">
                {{ props.column.label }}
                <i v-if="props.column.label !== 'S.no'" style="margin-left: 10px" :class="store.settings.sortIcons[col.sort]"></i>
              </div>
            </template>

            <template slot-scope="props">
              <div v-if="col.key === 'nextstrain_clade'">
                <el-tag :style="`background-color: ${store.theme.colors.nextstrain_clades[props.row.nextstrain_clade]};\
                border: 1px solid ${store.theme.colors.nextstrain_clades[props.row.nextstrain_clade]}; width: 80%;\
                font-weight: bold; text-align: center`" 
                effect="dark">{{ props.row.nextstrain_clade }}</el-tag>
              </div>
              <div v-else>
                {{ props.row[col.key] }}
              </div>
            </template>
          </el-table-column>

        </el-table>
      </el-col>
    </el-row>

    <el-dialog :visible.sync="filtersVisible" id="metadata-table-filters">
      <el-row slot="title" style="padding: 15px 20px 15px 20px; background-color: #4e4e4e; color: #dedede">
        <el-col style="font-family: 'HammerSmith One', sans-serif; font-size: 1.2em; padding: 0px">
          Metadata Filters
        </el-col>
      </el-row>

      <el-row>
        <el-row :gutter="20">

          <el-col>
            <el-row>
              <el-col style="margin-top: 20px" :span="22" :offset="1" align="left">
                <el-row style="padding: 5px 0px">
                  <el-checkbox v-model="store.metadataTable.tableFilters.cladeCheck" style="margin-right: 10px" >
                    <span style="font-weight: bold; font-size: 1.1em">Clade Filter</span>
                  </el-checkbox>
                </el-row>
                <el-radio-group v-model="store.metadataTable.tableFilters.cladeFilterType"
                  @change="store.metadataTable.tableFilters.cladeCheck=true" style="margin-left: 10px">
                  <el-radio label="nextstrain_clade">
                    <span style="font-weight: bold; margin-right: 5px"> Nextstrain </span>
                    <el-select v-model="store.metadataTable.tableFilters.nextstrain_clade" size="small" :collapse-tags="true" multiple>
                      <el-option v-for="c in store.constants.nextstrain_clades" :key="c" :label="c" :value="c"></el-option>
                    </el-select>
                  </el-radio>

                  <el-radio label="gisaid_clade" style="margin-top: 10px">
                    <span style="font-weight: bold; margin-right: 5px">GISAID </span>
                    <el-select v-model="store.metadataTable.tableFilters.gisaid_clade" size="small" :collapse-tags="true" multiple>
                      <el-option v-for="c in store.constants.gisaid_clades" :key="c" :label="c" :value="c"></el-option>
                    </el-select>
                  </el-radio>

                  <el-radio label="pangolin_lineage" style="margin-top: 10px">
                    <span style="font-weight: bold; margin-right: 5px"> Pangolin lineage </span>
                    <el-select v-model="store.metadataTable.tableFilters.pangolin_lineage" size="small" :collapse-tags="true" multiple>
                      <el-option v-for="c in store.constants.pangolin_lineage" :key="c" :label="c" :value="c"></el-option>
                    </el-select>
                  </el-radio>
                </el-radio-group>
              </el-col>
            </el-row>
          </el-col>


          <el-col style="margin-top: 20px" :span="22" :offset="1" align="left">
            <div style="margin-bottom: 8px">
              <el-checkbox v-model="store.metadataTable.tableFilters.genderCheck" style="margin-right: 10px" >
                <span style="font-weight: bold; font-size: 1.1em">Gender</span>
              </el-checkbox>
            </div>
            <el-select v-model="store.metadataTable.tableFilters.gender" size="small" :collapse-tags="true"
              @focus="store.metadataTable.tableFilters.genderCheck=true" multiple>
              <el-option v-for="g in store.constants.gender" :key="g" :label="g" :value="g"></el-option>
            </el-select>
          </el-col>

          <el-col style="margin-top: 20px" :span="22" :offset="1" align="left">
            <div style="margin-bottom: 8px">
              <el-checkbox v-model="store.metadataTable.tableFilters.ageCheck" style="margin-right: 10px" >
                <span style="font-weight: bold; font-size: 1.1em">Age</span>
              </el-checkbox>
            </div>
            <el-input-number style="width: 120px" size="small" controls-position="right" 
              @focus="store.metadataTable.tableFilters.ageCheck=true"
              @change="store.metadataTable.tableFilters.ageCheck=true"
              v-model="store.metadataTable.tableFilters.minAge" ></el-input-number>
            <span style="font-weight: bold; font-size: 1.2em; margin-left: 10px; margin-right: 10px">-</span>
            <el-input-number style="width: 120px" size="small" controls-position="right"
              @focus="store.metadataTable.tableFilters.ageCheck=true" 
              @change="store.metadataTable.tableFilters.ageCheck=true"
              v-model="store.metadataTable.tableFilters.maxAge" ></el-input-number>
          </el-col>

          <el-col style="margin-top: 20px" :span="22" :offset="1" align="left">
            <div style="margin-bottom: 8px">
              <el-checkbox v-model="store.metadataTable.tableFilters.dateCheck" style="margin-right: 10px" >
                <span style="font-weight: bold; font-size: 1.1em">Collection date</span>
              </el-checkbox>
            </div>
            <el-row justify="space-between">
              <el-col :span="11">
                <el-date-picker v-model="store.metadataTable.tableFilters.collectionDate_start" size="mini" type="date"
                  @focus="store.metadataTable.tableFilters.dateCheck=true" style="width: 100%" placeholder="Start date"> 
                </el-date-picker>
              </el-col>
              <el-col :span="2" style="text-align: center">-</el-col>
              <el-col :span="11">
                <el-date-picker v-model="store.metadataTable.tableFilters.collectionDate_end" size="mini" type="date"
                  @focus="store.metadataTable.tableFilters.dateCheck=true" style="width: 100%" placeholder="End date"> 
                </el-date-picker>
              </el-col>  
            </el-row>
          </el-col>

          <el-col style="margin-top: 20px" :span="22" :offset="1" align="left">
            <div style="margin-bottom: 8px">
              <el-checkbox v-model="store.metadataTable.tableFilters.divisionCheck" style="margin-right: 10px" >
                <span style="font-weight: bold; font-size: 1.1em">State</span>
              </el-checkbox>
            </div>
            <el-select v-model="store.metadataTable.tableFilters.division" size="small"
              @focus="store.metadataTable.tableFilters.divisionCheck=true" :collapse-tags="true" multiple>
              <el-option v-for="s in store.constants.indianStates" :key="s.code" :label="s.name" :value="s.name"></el-option>
            </el-select>
          </el-col>

          <el-col style="margin-top: 20px" :span="22" :offset="1" align="left">
            <div style="margin-bottom: 8px">
              <el-checkbox v-model="store.metadataTable.tableFilters.nCheck" style="margin-right: 10px" >
                <span style="font-weight: bold; font-size: 1.1em">Maximum N perncentage</span>
              </el-checkbox>
            </div>
            <el-input-number style="width: 120px" size="small" controls-position="right"
              @focus="store.metadataTable.tableFilters.nCheck=true" 
              @change="store.metadataTable.tableFilters.nCheck=true" 
              v-model="store.metadataTable.tableFilters.n_percent" ></el-input-number>
          </el-col>

          <el-col style="margin-top: 20px" :span="22" :offset="1" align="left">
            <div style="margin-bottom: 8px">
              <el-checkbox v-model="store.metadataTable.tableFilters.gapCheck" style="margin-right: 10px" >
                <span style="font-weight: bold; font-size: 1.1em">Maximum Gap perncentage</span>
              </el-checkbox>
            </div>
            <el-input-number style="width: 120px" size="small" controls-position="right"
              @focus="store.metadataTable.tableFilters.gapCheck=true"
              @change="store.metadataTable.tableFilters.gapCheck=true" 
              v-model="store.metadataTable.tableFilters.gap_percent" ></el-input-number>
          </el-col>

          <el-col style="margin-top: 20px" :span="22" :offset="1" align="right">
            <el-button size="medium" type="success" @click="resetFilters()">Reset</el-button>
            <el-button size="medium" type="primary" @click="applyFilters()">Apply</el-button>
          </el-col>
        </el-row>
      </el-row>
    </el-dialog>
  </div>
</template>

<script>
  import { store } from '@/scripts/store.js';
  import { downloadTSV } from '@/scripts/utils.js'

  const _ = require('lodash');
  
  export default {
    name: 'MetadataTable',
    props: { },
    components: { },
    data() {
      
      return {
        store: store,
        columns: store.metadataTable.tableSettings.columns,
        minAge: 0, maxAge: 120,
        paginationLayout: 'total, sizes, prev, pager, next, jumper',
        visibleColumns: _.filter(store.metadataTable.tableSettings.columns, 'show'),
        hiddenColumns: _.filter(store.metadataTable.tableSettings.columns, o => !o.show),
        filtersVisible: false,
      }
    },
    computed: {
      cladeIndex: function(){
        return _.findIndex(this.visibleColumns, { key: 'clade' })
      },
      headerCellStyle() {
        const borderColor = store.theme.viewMode.tableBorder[store.theme.viewMode.active]
        return { backgroundColor: '#84a42e', borderRight: `1px solid ${borderColor}`, color: '#eeeeee', borderBottom: 'none'}
      } 
    },
    created() {
      window.addEventListener('resize', this.handleResize);
      this.handleResize();
    },
    destroyed() {
      window.removeEventListener('resize', this.handleResize)
    },
    mounted() {
      // this.loadTable()
    },
    methods: {
      // loadTable: function() {
      //   console.log('Loading Metadata table!')
      // },
      sortTable: function(val, column) {
        if (column.property !== 'index') {
          store.metadataTable.methods.sortTable(column.property)
        }
      },
      handleResize: function() {
        const context = this
        if (screen.width < 600) { context.paginationLayout = 'total, sizes, jumper' } 
        else if ( screen.width < 900 ) { context.paginationLayout = 'total, sizes, jumper, prev, next' }
        else { context.paginationLayout = 'total, sizes, prev, pager, next, jumper' }
      },
      flagColor(data) {
        if (data.row.n_percent < 1 && data.row.gap_percent < 1) { return 'green' }
        else if ( data.row.n_percent < 5 && data.row.gap_percent < 5 ) { return 'orange' }
        else { return 'red' }
      },
      flagContent(data) {
        if (data.row.N_percent < 1) { return 'OK' }
        else { 
          return `Assembly N(%) : ${data.row.n_percent} 
          || Assembly Gap(%) : ${data.row.gap_percent}`
        }
      },
      reloadTable() {
        const context = this;
        store.metadataTable.tableSettings.show = false
        context.visibleColumns = _.filter(store.metadataTable.tableSettings.columns, 'show')
        context.hiddenColumns = _.filter(store.metadataTable.tableSettings.columns, o => !o.show)
        setTimeout(() => store.metadataTable.tableSettings.show = true, 100)
      },
      downloadMetadataTSV() {
        const context = this
        const delimiter = "\t"
        const columns = _.map(context.visibleColumns, 'key')
        const content = [columns.join(delimiter)]
        store.metadataTable.tableSettings.tableData.forEach( d => {
          const row = []
          columns.forEach(c => { row.push(d[c]) })
          content.push(row.join(delimiter))
        })
        downloadTSV(content.join('\n'), 'Gear19_Metadata.tsv')
      },
      applyFilters() {
        store.metadataTable.methods.applyFilters()
        this.filtersVisible = false
      },
      resetFilters() {
        store.metadataTable.methods.resetFilters()
      }
    }
  }
</script>

<style>

  #metadata-table {
    background-color: rgba(0,0,0,0);
    border: none;
  }

  #metadata-table .el-table__header-wrapper .el-table__header thead tr {
    background-color: rgba(0,0,0,0);
  }

  #metadata-table .el-table__body-wrapper .el-table__body tbody tr {
    background-color: rgba(255,255,255,0.7) !important;
  }

  #metadata-table-body .el-col .el-pagination .el-pagination__total {
    vertical-align: center;
    background-color: rgba(255,255,255,0.2);
    box-sizing: unset;
    border-radius: 5px;
    padding: 3px 10px;
    color: #84a42e;
    font-weight: bold;
  }

  #metadata-table-body .el-col .el-pagination .el-pager li.number.active {
    color: #84a42e;
  }

  #metadata-table-body .el-col .el-pagination .btn-next {
    background-color: rgba(255,255,255,0.2);
    border-radius: 5px;
    margin-left: 5px;
    padding: 3px;
  }

  #metadata-table-body .el-col .el-pagination .el-pager .btn-quicknext {
    background-color: rgba(255,255,255,0.2);
    border-radius: 5px;
    padding: 0px 3px 0px 3px;
  }

  #metadata-table-body .el-col .el-pagination .el-pager .btn-quicknext:hover {
    color: #84a42e;
  }

  #metadata-table-body .el-col .el-pagination .btn-next:hover {
    color: #84a42e;
  }

  #metadata-table-body .el-col .el-pagination .btn-prev {
    background-color: rgba(255,255,255,0.2);
    border-radius: 5px;
    padding: 3px;
  }

  #metadata-table-body .el-col .el-pagination .el-pager .btn-quickprev {
    background-color: rgba(255,255,255,0.2);
    border-radius: 5px;
    padding: 0px 3px 0px 3px;
  }

  #metadata-table-body .el-col .el-pagination .el-pager .btn-quickprev:hover {
    color: #84a42e;
  }

  #metadata-table-body .el-col .el-pagination .btn-prev:hover {
    color: #84a42e;
  }

  #metadata-table-body .el-col .el-pagination .el-pager * {
    background-color: rgba(0,0,0,0);
  }

  #metadata-table-filters .el-dialog .el-dialog__header {
    padding: 0px;
  }

  #metadata-table-filters .el-dialog {
    max-width: 500px;
  }
</style>